import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchLeagueLadder } from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { TierBadge } from "./TierBadge";
import { VerifiedCheck } from "./VerifiedCheck";

/**
 * LeagueLadderPage — public `/rankings/leagues/:slug` view.
 *
 * Reads the public `GET /api/leagues/by-slug/:slug/ladder` endpoint and
 * renders every team sorted by MMR desc with TierBadge + W/L/win-rate.
 * Designed as a conversion magnet — share the link to convince players
 * to join the league. No auth required to view.
 */
export function LeagueLadderPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchLeagueLadder(slug)
      .then((d) => { setData(d); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load ladder."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <main className="page-z" data-testid="league-ladder-loading">
        <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
          <p className="text-[11px] tracking-[3px] text-[#14b8a6] animate-pulse">
            LOADING LADDER…
          </p>
        </div>
      </main>
    );
  }

  if (err || !data) {
    return (
      <main className="page-z">
        <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
          <p className="text-[11px] tracking-[3px] text-[#14b8a6]" data-testid="league-ladder-error">
            {err || "Ladder unavailable."}
          </p>
          <Link
            to="/leagues"
            className="inline-block mt-4 text-[11px] tracking-widest text-[#14b8a6] hover:text-white"
          >
            ← BROWSE LEAGUES
          </Link>
        </div>
      </main>
    );
  }

  const { league, rows, total_teams } = data;

  return (
    <main className="page-z" data-testid="league-ladder-page">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
        {/* Hero */}
        <div
          className="glass rounded-3xl overflow-hidden relative border border-white/10"
          data-testid="league-ladder-hero"
        >
          <div
            className="h-40 lg:h-56 relative bg-gradient-to-br from-[#14b8a6]/30 to-[#14b8a6]/20"
            style={league.banner_url ? {
              backgroundImage: `url(${league.banner_url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            } : {}}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between">
              <div className="min-w-0">
                <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
                  <i className="fas fa-trophy mr-1" />LEAGUE LADDER
                </p>
                <h1 className="font-display text-3xl lg:text-5xl grad-text mt-1 truncate">
                  {league.name}
                </h1>
                <p className="text-[12px] text-[#a3a3ac] mt-1">
                  {league.game} · {total_teams} TEAM{total_teams === 1 ? "" : "S"} · {league.league_type === "PRO" ? "👑 PRO LEAGUE" : "CASUAL"}
                </p>
              </div>
              <Link
                to={`/?league=${league.slug}`}
                data-testid="ladder-view-league-btn"
                className="hidden lg:inline-flex items-center gap-2 text-[10px] tracking-widest px-4 py-2 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
              >
                <i className="fas fa-arrow-right-to-bracket" />VIEW LEAGUE
              </Link>
            </div>
          </div>
        </div>

        {/* Ladder */}
        <div className="mt-6 glass rounded-3xl overflow-hidden border border-white/10">
          {rows.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-[11px] tracking-[3px] text-white/40" data-testid="league-ladder-empty">
                NO TEAMS HAVE PLAYED YET — BE THE FIRST TO CLIMB.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="league-ladder-table">
              <thead>
                <tr className="text-[10px] tracking-widest text-white/45 border-b border-white/10">
                  <th className="text-left px-4 py-3 w-12">#</th>
                  <th className="text-left px-2 py-3">TEAM</th>
                  <th className="text-left px-2 py-3 hidden md:table-cell">TIER</th>
                  <th className="text-center px-2 py-3 w-16">W</th>
                  <th className="text-center px-2 py-3 w-16">L</th>
                  <th className="text-center px-2 py-3 w-20 hidden sm:table-cell">WIN %</th>
                  <th className="text-right px-4 py-3 w-20">MMR</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.team_id}
                    data-testid={`ladder-row-${r.team_id}`}
                    className="border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-white/65">
                      {r.rank <= 3 ? (
                        <span className="text-[#14b8a6] font-bold">
                          {["🥇","🥈","🥉"][r.rank - 1]}
                        </span>
                      ) : r.rank}
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <AvatarChip avatarId={r.avatar_id} pfpUrl={r.pfp_url} size={32} />
                        <div className="min-w-0">
                          <p className="text-white font-bold truncate flex items-center gap-1">
                            <span className="truncate">{r.name}</span>
                          </p>
                          {r.captain && (
                            <p className="text-[10px] text-white/45 truncate flex items-center gap-1">
                              <i className="fas fa-crown text-[#14b8a6]/70 text-[8px]" />
                              {r.captain.name}
                              <VerifiedCheck user={r.captain} className="ml-0.5" />
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 hidden md:table-cell">
                      <TierBadge tier={r.tier} mmr={null} />
                    </td>
                    <td className="px-2 py-3 text-center text-[#14b8a6] font-mono">
                      {r.wins}
                    </td>
                    <td className="px-2 py-3 text-center text-white/55 font-mono">
                      {r.losses}
                    </td>
                    <td className="px-2 py-3 text-center text-white/65 font-mono hidden sm:table-cell">
                      {(r.win_rate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white font-bold">
                      {r.mmr}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* CTA — driftleague.co convert-on-page-bottom hook */}
        <div className="mt-6 glass rounded-3xl p-6 border border-[#14b8a6]/30 text-center">
          <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">READY TO CLIMB?</p>
          <p className="text-white/75 text-sm mt-2 max-w-md mx-auto">
            Build a team in <b className="text-white">{league.name}</b>, challenge opponents, and earn your way up the {league.game} ladder.
          </p>
          <Link
            to="/leagues"
            data-testid="ladder-cta-join"
            className="inline-flex items-center gap-2 mt-4 text-[11px] tracking-widest px-5 py-3 rounded-xl bg-[#14b8a6] text-black font-bold hover:brightness-110"
          >
            <i className="fas fa-rocket" />JOIN A LEAGUE
          </Link>
        </div>
      </div>
    </main>
  );
}

export default LeagueLadderPage;
